import React, { useCallback, useRef, useState } from 'react';
import { LeftOutlined } from '@ant-design/icons';
import { ConfigProvider } from 'antd';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { WizardJumpContext } from './components/WizardNav';
import ResultView from './review/ResultView';
import ReviewStep from './review/ReviewStep';
import ObjectiveStep from './steps/ObjectiveStep';
import TypeStep from './steps/TypeStep';
import ActionStep from './triggers/ActionStep';
import TriggerListStep from './triggers/TriggerListStep';
import TriggerStep from './triggers/TriggerStep';
import {
  GameLimitsStep,
  GameModePills,
  GameOutcomesStep,
  GameProbabilityStep,
  GameTypeStep
} from './types/game/GameSteps';
import { WedgeCountStep, WedgeOutcomesStep } from './types/game/WheelSteps';
import { InstantLimitsStep, InstantPrizeStep } from './types/instant/InstantSteps';
import MilestoneStep from './types/progress/MilestoneStep';
import { LevelPrizeStep, ProgressLimitsStep } from './types/progress/ProgressSteps';
import { QuestLimitsStep, QuestPrizeStep } from './types/quest/QuestSteps';
import StampLimitsStep from './types/stamp/StampLimitsStep';
import StampPrizeStep from './types/stamp/StampPrizeStep';
import {
  IWizardStepProps,
  jumpToStep,
  nextStep,
  previousStep,
  WizardCampaignType,
  WizardStep
} from './wizardModel';
import { useCampaignWizard } from './wizardStore';
import { LinkButton, WizardRoot } from './wizardStyles';
import {
  CampaignWizardIntent,
  CampaignWizardResult,
  CampaignWizardSubmit,
  WizardHandoff
} from './wizardSubmission';

type StepTable = Partial<Record<WizardStep, React.FC<IWizardStepProps>>>;

const SHARED_STEPS: StepTable = {
  type: TypeStep,
  objective: ObjectiveStep,
  action: ActionStep,
  trigger: TriggerStep
};

// The steps whose screen differs by campaign type.
const TYPE_STEPS: Record<WizardCampaignType, StepTable> = {
  stamp: { prizes: StampPrizeStep, limits: StampLimitsStep },
  quest: { tasks: TriggerListStep, prizes: QuestPrizeStep, limits: QuestLimitsStep },
  progress: {
    milestones: MilestoneStep,
    rules: TriggerListStep,
    prizes: LevelPrizeStep,
    limits: ProgressLimitsStep
  },
  game: {
    game: GameTypeStep,
    wedges: WedgeCountStep,
    wedgeOutcomes: WedgeOutcomesStep,
    outcomes: GameOutcomesStep,
    probability: GameProbabilityStep,
    limits: GameLimitsStep
  },
  instant_outcome: { prizes: InstantPrizeStep, limits: InstantLimitsStep }
};

// Not drawn: the frames have no way back to the conversation; it sits above the wizard.
const ExitRow = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: -46px auto 22px;
`;

interface ICampaignWizardProps {
  onSubmit: CampaignWizardSubmit;
  /** Back to the conversation the wizard was opened over; the session is untouched. */
  onExit: () => void;
  onHandoff: WizardHandoff;
}

interface ISaved {
  name: string;
  intent: CampaignWizardIntent;
  result: CampaignWizardResult;
}

/** The standalone Create a Campaign wizard (Figma 6566:9267), one step at a time. */
const CampaignWizard: React.FC<ICampaignWizardProps> = ({ onSubmit, onExit, onHandoff }) => {
  const { t } = useMultiLanguageTranslation();
  const { state, update, reset } = useCampaignWizard();
  const [saved, setSaved] = useState<ISaved | null>(null);
  const jump = useCallback(
    (number: number) => update(current => jumpToStep(current, number)),
    [update]
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const next = useCallback(
    () => update(current => ({ ...current, step: nextStep(current) })),
    [update]
  );
  const back = useCallback(
    () => update(current => ({ ...current, step: previousStep(current) })),
    [update]
  );
  const popupContainer = useCallback(() => rootRef.current ?? document.body, []);
  const props = { state, update, next, back };
  const Step =
    (state.type !== null ? TYPE_STEPS[state.type][state.step] : undefined) ??
    SHARED_STEPS[state.step] ??
    TypeStep;

  // A save clears the stored answers; the result stays until the user moves on.
  const onSaved = (intent: CampaignWizardIntent, result: CampaignWizardResult) => {
    setSaved({ name: state.name.trim(), intent, result });
    reset();
    onHandoff({
      entity: 'campaign',
      id: result.campaignId,
      pendingRequestId: result.requestId,
      savedAsDraft: intent === 'draft'
    });
  };

  return (
    <ConfigProvider getPopupContainer={popupContainer}>
      <WizardRoot ref={rootRef} data-testid="orbit-wizard" data-step={state.step}>
        <ExitRow>
          <LinkButton type="button" onClick={onExit} data-testid="orbit-wizard-exit">
            <LeftOutlined aria-hidden="true" style={{ fontSize: 10 }} />
            <span>{t(`${TOrbit}.wizard.exit`)}</span>
          </LinkButton>
        </ExitRow>
        {saved !== null ? (
          <ResultView
            name={saved.name}
            intent={saved.intent}
            result={saved.result}
            onCreateAnother={() => setSaved(null)}
            onExit={onExit}
          />
        ) : (
          <WizardJumpContext.Provider value={jump}>
            {state.step === 'review' ? (
              <ReviewStep
                {...props}
                onSubmit={onSubmit}
                onSaved={onSaved}
                header={
                  state.type === 'game' ? <GameModePills state={state} update={update} /> : null
                }
              />
            ) : (
              <Step key={`${state.step}-${state.editing}`} {...props} />
            )}
          </WizardJumpContext.Provider>
        )}
      </WizardRoot>
    </ConfigProvider>
  );
};

export default CampaignWizard;
